// PARCOURS DE DÉMARRAGE — la compréhension du projet, en pleine page.
//
// Un projet VIDE n'a rien à montrer : un tableau de bord sans score et neuf
// entrées de menu vers des écrans vides donnent l'image d'un outil compliqué.
// Tant que rien n'est saisi, on ne montre donc QUE ceci : pas de menu, pas de
// filtre, pas d'en-tête d'application.
//
// Le parcours se réduit à l'ÉTAT DES LIEUX : un mot d'accueil, une étape, et
// l'on arrive sur le tableau de bord. Le reste de la mesure (questionnaire,
// fonctionnalités, utilisateurs, bilan, piliers) s'y complète, carte par carte
// (`ProjectTodo`) — demander tout d'un coup décourageait avant d'avoir commencé.
//
// `ProjectGate` décide d'y entrer UNE fois, à l'arrivée sur le projet.
import { useEffect, useState, type ReactNode } from 'react';
import { useProductContext } from '../api/queries';
import type { ProductContext } from '../api/types';
import { Spinner } from '../components/ui';
import { LogoMark, Wordmark } from '../components/Logo';
import { Mesure0Wizard } from '../mesure0/Mesure0Wizard';
import { Link } from '../router';
import { useCanWrite } from '../write/useCanWrite';
import './kickoff.css';

/**
 * Aiguillage d'un projet : parcours de démarrage s'il est vide, sinon le projet
 * lui-même (`children`). Qui ne peut pas écrire voit toujours le projet — on ne
 * lui montre pas un formulaire qu'il ne pourrait pas enregistrer.
 */
export function ProjectGate({
  projectId,
  projectName,
  children,
}: {
  projectId: string;
  projectName: string;
  children: ReactNode;
}) {
  const context = useProductContext(projectId);
  const canWrite = useCanWrite(projectId);
  // Décision prise à l'arrivée sur CE projet, puis tenue jusqu'à la sortie du
  // parcours (même motif que `ProjectFilterProvider` au changement de projet).
  const [mode, setMode] = useState<{ projectId: string; kickoff: boolean } | null>(null);
  if (context.data && mode?.projectId !== projectId) {
    setMode({ projectId, kickoff: context.data.empty && canWrite });
  }

  // Contexte indisponible : on ne retient pas le projet derrière une erreur.
  if (context.isError) return <>{children}</>;
  if (!context.data || mode?.projectId !== projectId) {
    return (
      <div className="app-loading">
        <Spinner label="Chargement…" />
      </div>
    );
  }
  if (!mode.kickoff) return <>{children}</>;

  return (
    <KickoffPage
      projectId={projectId}
      projectName={projectName}
      context={context.data}
      onDone={() => setMode({ projectId, kickoff: false })}
    />
  );
}

function KickoffPage({
  projectId,
  projectName,
  context,
  onDone,
}: {
  projectId: string;
  projectName: string;
  context: ProductContext;
  onDone: () => void;
}) {
  // La page recouvre l'application : c'est elle qui défile, pas l'arrière-plan.
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  return (
    <div className="kickoff">
      <header className="kickoff-head">
        <span className="app-brand">
          <LogoMark />
          <Wordmark className="app-brand-name" />
        </span>
        <p className="kickoff-project">
          <span className="t-mono">Nouveau projet</span>
          {projectName}
        </p>
        <Link to="/" className="kickoff-quit">
          Quitter
        </Link>
      </header>
      <main className="kickoff-main">
        <Mesure0Wizard
          projectId={projectId}
          projectName={projectName}
          context={context}
          welcome
          page
          only="product"
          onClose={onDone}
        />
      </main>
    </div>
  );
}
